import type { ReactNode } from "react"
import type { CartesianViewBoxRequired } from "recharts/types/util/types"
import Tooltip from "@/v2-ui/tooltip"
import { DATA_VIZ } from "@/v2-ui/color/dataViz.colors"
import { usePrimitiveText } from "@/v2-ui/text/offline"

type CustomBarMarkerData = {
  x?: number | string
  y?: number | string
  viewBox: CartesianViewBoxRequired
  number?: number
}

type ShapeMarkerConfig = {
  size?: number
}

type TextMarkerConfig = {
  fontSize?: number
  fontWeight?: number | string
  color?: string
}

type LineLabelMarkerConfig = {
  lineOffsetX?: number
  labelOffsetY?: number
  dashedLine?: boolean
}

type MarkerShape = {
  type: "shape"
  config?: ShapeMarkerConfig
}

type MarkerText = {
  type: "text"
  config?: TextMarkerConfig
}

type MarkerLineLabel = {
  type: "line-label"
  config?: LineLabelMarkerConfig
}

type MarkerConfig = MarkerShape | MarkerText | MarkerLineLabel

type CustomBarMarkerProps = CustomBarMarkerData & {
  label?: string
  marker: MarkerConfig
  activeBarIndexes: number[]
  markerColor?: string[]
  layout?: "horizontal" | "vertical"
  content?: ReactNode | ((activeMarkerPosition: number) => ReactNode)
  markerPositionOffset?: { x: number; y: number }
  chartBarMaxLength?: number
}

const DEFAULT_COLOR = DATA_VIZ.brand.green2
const MARKER_WIDTH_OFFSET = 10
const MARKER_HEIGHT_OFFSET = 20
const LINE_LABEL_EDGE_GUARD = 3
const STACKED_LABEL_Y_OFFSET = 30
const LABEL_BOX_WIDTH = 120
const LABEL_BOX_HEIGHT = 40
const LABEL_BOX_Y_OFFSET = 10

type MarkerPositionOptions = {
  index: number
  count: number
  layout: "horizontal" | "vertical"
  x: number
  y: number
  width: number
  height: number
}

/**
 * Calculates the position of a marker within a bar chart.
 *
 * @param i - The index of the current marker.
 * @param count - The total number of markers.
 * @param layout - The layout orientation of the chart, either "horizontal" or "vertical".
 * @param x - The x-coordinate of the bar's starting position.
 * @param y - The y-coordinate of the bar's starting position.
 * @param width - The width of the bar.
 * @param height - The height of the bar.
 * @returns An object containing the x and y coordinates of the marker's position.
 */
const getMarkerPosition = (positionOpt: MarkerPositionOptions) => {
  const { count, height, index, layout, width, x, y } = positionOpt
  const offset = (count - 1) * MARKER_HEIGHT_OFFSET
  return layout === "horizontal"
    ? {
        x: x + width / 2 - offset / 2 + index * MARKER_HEIGHT_OFFSET,
        y: y - MARKER_WIDTH_OFFSET,
      }
    : {
        x: x + width + MARKER_WIDTH_OFFSET,
        y: y + height / 2 - offset / 2 + index * MARKER_HEIGHT_OFFSET,
      }
}

/**
 * Calculates the side of the label that should be rendered.
 *
 * The side of the label is determined by the following rules:
 * 1. If the index is 0 or less than LINE_LABEL_EDGE_GUARD, the label is rendered on the right side.
 * 2. If the index is greater than or equal to LINE_LABEL_EDGE_GUARD and the chartBarMaxLength is defined, the label is rendered on the left side.
 * 3. If the index is greater than or equal to LINE_LABEL_EDGE_GUARD but the chartBarMaxLength is not defined, the label is rendered on the right side.
 * 4. If the fixedSide is defined, the label is rendered on the fixed side.
 * 5. If none of the above conditions are met, the label is rendered on the side determined by the activeMarkerPosition.
 *
 * @param index - The index of the current marker.
 * @param chartBarMaxLength - The maximum length of the bar chart.
 * @param fixedSide - The fixed side of the label.
 * @param activeMarkerPosition - The position of the active marker.
 * @returns The side of the label that should be rendered.
 */
const getLabelSide = (
  index: number,
  chartBarMaxLength: number | undefined,
  fixedSide: "left" | "right" | undefined,
  activeMarkerPosition: number,
): "left" | "right" => {
  if (index === 0 || index < LINE_LABEL_EDGE_GUARD) return "right"
  if (
    chartBarMaxLength &&
    (index === chartBarMaxLength ||
      index > chartBarMaxLength - LINE_LABEL_EDGE_GUARD)
  )
    return "left"
  if (fixedSide) return fixedSide
  return activeMarkerPosition % 2 === 0 ? "left" : "right"
}

function CustomBarMarker(props: CustomBarMarkerProps) {
  const { getText } = usePrimitiveText()
  const {
    label = getText("UI_Primitive_CustomBarMarker_Label").Name,
    x = 0,
    y = 0,
    number = 0,
    marker,
    activeBarIndexes = [],
    markerColor = [],
    layout = "horizontal",
    content,
    viewBox,
    markerPositionOffset = { x: 5, y: 5 },
    chartBarMaxLength,
  } = props

  if (!activeBarIndexes.includes(number)) return null

  // SHAPE MARKER
  if (marker.type === "shape") {
    const { size = 10 } = marker.config ?? {}
    const circlesForThisBar = activeBarIndexes.filter((aI) => aI === number)

    return (
      <>
        {circlesForThisBar.map((_, i) => {
          const { x: cx, y: cy } = getMarkerPosition({
            index: i,
            count: circlesForThisBar.length,
            layout,
            x: Number(x),
            y: Number(y),
            width: viewBox.width,
            height: viewBox.height,
          })
          const activeMarkerPosition = i || activeBarIndexes.indexOf(number)
          const fillColor = markerColor[activeMarkerPosition] ?? DEFAULT_COLOR
          const isCircle = activeMarkerPosition % 2 === 0
          const markerContent =
            typeof content === "function"
              ? content(activeMarkerPosition)
              : content

          return (
            <svg
              key={`${number}-${i}`}
              x={cx - markerPositionOffset.x}
              y={cy - markerPositionOffset.y}
              width={size}
              height={size}
              viewBox="0 0 12 12"
            >
              <foreignObject width="100%" height="100%">
                <Tooltip>
                  <Tooltip.Trigger className="cursor-pointer">
                    <svg width="100%" height="100%">
                      {isCircle ? (
                        <circle cx={6} cy={6} r={6} fill={fillColor} />
                      ) : (
                        <polygon
                          points={
                            layout === "horizontal"
                              ? "6,0 12,12 0,12"
                              : "0,0 12,6 0,12"
                          }
                          fill={fillColor}
                        />
                      )}
                    </svg>
                  </Tooltip.Trigger>
                  <Tooltip.Content className="bg-background shadow-xl border border-border/50">
                    {markerContent}
                  </Tooltip.Content>
                </Tooltip>
              </foreignObject>
            </svg>
          )
        })}
      </>
    )
  }

  // TEXT MARKER
  if (marker.type === "text") {
    const { x: textX, y: textY } = getMarkerPosition({
      index: 0,
      count: 1,
      layout,
      x: Number(x),
      y: Number(y),
      width: viewBox.width,
      height: viewBox.height,
    })
    return (
      <text
        x={textX}
        y={textY}
        fill="black"
        textAnchor="middle"
        dominantBaseline="middle"
      >
        {label}
      </text>
    )
  }

  // LINE-LABEL MARKER
  if (marker.type === "line-label") {
    const {
      dashedLine = true,
      lineOffsetX = 4,
      labelOffsetY = 10,
    } = marker.config ?? {}

    const matchingMarkers = activeBarIndexes
      .map((aI, i) => ({ indexBar: aI, activeMarkerPosition: i }))
      .filter(({ indexBar }) => indexBar === number)

    let fixedSide: "left" | "right" | undefined
    if (
      activeBarIndexes.length === 2 &&
      activeBarIndexes[0] !== activeBarIndexes[1]
    ) {
      const [a, b] = activeBarIndexes
      const firstIsGreater = a > b
      if (number === a) fixedSide = firstIsGreater ? "right" : "left"
      if (number === b) fixedSide = firstIsGreater ? "left" : "right"
    }

    const allLabelSides = activeBarIndexes.map((barIndex, i) =>
      getLabelSide(barIndex, chartBarMaxLength, undefined, i),
    )

    const forceStacked = allLabelSides.every(
      (side) => side === allLabelSides[0],
    )

    return (
      <>
        {matchingMarkers.map(({ activeMarkerPosition }, i) => {
          const strokeColor = markerColor[activeMarkerPosition] ?? DEFAULT_COLOR
          const labelContent =
            typeof content === "function"
              ? content(activeMarkerPosition)
              : content
          const strokeDasharray =
            dashedLine && activeMarkerPosition % 2 !== 0 ? "4,2" : undefined

          const offset = (matchingMarkers.length - 1) * lineOffsetX
          const markerX =
            Number(x) + viewBox.width / 2 - offset / 2 + i * lineOffsetX

          const side = getLabelSide(
            number,
            chartBarMaxLength,
            fixedSide,
            activeMarkerPosition,
          )
          const isLeft = side === "left"
          const labelX = markerX + (isLeft ? -lineOffsetX : lineOffsetX)
          const stackedLabelY = forceStacked
            ? labelOffsetY + i * STACKED_LABEL_Y_OFFSET
            : labelOffsetY

          return (
            <g key={`line-label-${number}-${i}`}>
              <line
                x1={markerX}
                x2={markerX}
                y2={y}
                stroke={strokeColor}
                strokeWidth={2}
                strokeDasharray={strokeDasharray}
              />
              <line
                x1={markerX}
                y1={y}
                x2={markerX}
                y2={Number(y) + viewBox.height}
                stroke="#fff"
                strokeWidth={2}
                strokeDasharray={strokeDasharray}
              />
              <foreignObject
                x={isLeft ? labelX - LABEL_BOX_WIDTH : labelX}
                y={stackedLabelY - LABEL_BOX_Y_OFFSET}
                width={LABEL_BOX_WIDTH}
                height={LABEL_BOX_HEIGHT}
              >
                <div
                  className="text-xs font-bold"
                  style={{
                    color: strokeColor,
                    textAlign: isLeft ? "right" : "left",
                  }}
                >
                  {labelContent}
                </div>
              </foreignObject>
            </g>
          )
        })}
      </>
    )
  }

  return null
}

export default CustomBarMarker
